import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/components/Seo";
import { Users } from "lucide-react";

export default function InvitePage() {
  const { code } = useParams();
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | invalid | sent | error
  const [message, setMessage] = useState("");

  useEffect(() => {
    api.get(`/invite/${encodeURIComponent(code)}`).then((r) => { setInfo(r.data); setState("ready"); }).catch(() => setState("invalid"));
  }, [code]);

  const send = async () => {
    try { await api.post(`/invite/${encodeURIComponent(code)}/accept`); setState("sent"); }
    catch (e) { setMessage(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Something went wrong. Please try again."); setState("error"); }
  };

  return (
    <div className="mx-auto max-w-lg px-4 py-16 text-center">
      <Seo title="Friend invite" noindex />
      <Users className="mx-auto h-10 w-10 text-[#C89B3C]" />
      {state === "loading" && <p className="mt-4 text-muted-foreground">Loading…</p>}
      {state === "invalid" && (
        <>
          <h1 className="mt-4 font-serif text-3xl font-semibold">This invite isn't valid</h1>
          <p className="mt-2 text-muted-foreground">The link may have been reset. Ask your friend for a new one.</p>
          <Link to="/" className="mt-6 inline-flex rounded-full bg-[#C89B3C] px-6 py-2.5 text-sm font-semibold text-black">Go to My Theatre</Link>
        </>
      )}
      {(state === "ready" || state === "error") && info && (
        <>
          <h1 className="mt-4 font-serif text-3xl font-semibold" data-testid="invite-title">{info.username} invited you to be friends</h1>
          <p className="mt-2 text-muted-foreground">On My Theatre you can share the shows you've seen with friends, and see theirs. {info.username} will need to accept your request.</p>
          {state === "error" && <p className="mt-4 text-sm text-red-400" data-testid="invite-error">{message}</p>}
          {user ? (
            <button type="button" onClick={send} data-testid="invite-send" className="mt-6 rounded-full bg-[#C89B3C] px-8 py-3 text-sm font-semibold text-black">Send friend request</button>
          ) : (
            <Link to="/login" state={{ from: { pathname: `/invite/${code}` } }} data-testid="invite-login" className="mt-6 inline-flex rounded-full bg-[#C89B3C] px-8 py-3 text-sm font-semibold text-black">Sign in or create an account</Link>
          )}
        </>
      )}
      {state === "sent" && (
        <>
          <h1 className="mt-4 font-serif text-3xl font-semibold" data-testid="invite-sent">Request sent 🎭</h1>
          <p className="mt-2 text-muted-foreground">You'll see {info?.username} in your Friends tab once they accept.</p>
          <Link to="/watchlist" className="mt-6 inline-flex rounded-full bg-[#C89B3C] px-6 py-2.5 text-sm font-semibold text-black">Go to My Theatre</Link>
        </>
      )}
    </div>
  );
}